"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { Separator } from "@/components/ui/separator";
import { SectionWrapper } from "../components/SectionWraper";

interface ContentBlockProps {
  title?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  listItems?: {
    icon?: string;
    title?: string;
    crossOut?: boolean;
  }[];
  className?: string;
}

interface Compare1Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  themeChangeOnScroll?: boolean;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  badge?: string;
  title?: string;
  content?: string;
  leftContent?: ContentBlockProps;
  rightContent?: ContentBlockProps;
  attributes?: React.HTMLProps<HTMLElement>;
}

const ContentBlock = ({
  title,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  listItems,
  className
}: ContentBlockProps) => (
  <div className={cn(className, "rounded-xl border p-6")}>
    {title && (
      <h6 className="text-center">
        {parse(title)}
      </h6>
    )}
    <Separator className="my-6" />
    {listItems && (
      <ul className="flex flex-col gap-4 paragraph-lg mt-0 md:mt-10">
        {listItems.map((item, index) => (
          <li key={index} className={cn("flex items-center gap-4", item.crossOut && "opacity-50")}>
            {item.icon && (
              <span className="material-symbols-outlined">
                {item.icon}
              </span>
            )}
            <span className={cn(item.crossOut && "line-through")}>{item.title}</span>
          </li>
        ))}
      </ul>
    )}
    {hasButton && (
      <>
        <Button asChild variant={buttonStyle} size="lg" className="w-full mt-6 md:mt-10">
          <Link href={buttonLink || ""}>
            {buttonText}
          </Link>
        </Button>
      {microCopy && (
        <p className="paragraph-sm  text-muted-foreground text-center mt-3">
          {microCopy}
        </p>
      )}
      </>
    )}
    
  </div>
);


const Compare1 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Compare 1",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  leftContent,
  rightContent,
  attributes,
}: Compare1Props) => {
  
  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="compare1"
      attributes={attributes}
    >
      <div className="container"> 
        <div className="flex flex-col gap-6 text-center justify-center" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold className="m-auto">
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground max-w-[58ch] mx-auto">
              {parse(content)}
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-12 md:mt-20 lg:mt-24" data-aos="fade-up" data-aos-delay="100">
          <ContentBlock {...leftContent} className={cn("bg-background shadow")} />
          <ContentBlock {...rightContent} className={cn("bg-muted")} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Compare1 };